import { Button, Result, Spin } from 'antd';
import ApiAuthorizationRoutes from 'lib/api-authorization/ApiAuthorizationRoutes';
import AuthorizeRoute from 'lib/api-authorization/AuthorizeRoute';
import authService from 'lib/api-authorization/AuthorizeService';
import React, { lazy, Suspense, useCallback, useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes } from 'react-router-dom';
import SelectMunicipality from 'routes/SelectMunicipality/SelectMunicipality';
import TenantHandler from 'routes/TenantHandler/TenantHandler';
import styled from 'styled-components';
import { getLicense, getTenant, getTenants } from 'utils/api';
import './custom.css';

const CenterDiv = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
`;

const Dashboard = lazy(() => import('routes/TenantHandler/Dashboard/Dashboard'));
const CaseHandler = lazy(() => import('routes/TenantHandler/CaseHandler/CaseHandler'));
const AdminSettings = lazy(() => import('routes/TenantHandler/AdminSettings/AdminSettings'));

const App = () => {
  const [tenants, setTenants] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [user, setUser] = useState();

  useEffect(() => {
    authService.getUser().then(setUser);
  }, []);
  const [backendError, setBackendError] = useReducer((state, newState) => ({ ...state, ...newState }), {
    active: false,
    status: '500' //'success' | 'error' | 'info' | 'warning'| '404' | '403' | '500'
  });

  const [t] = useTranslation();

  const onLogout = () => {
    window.location = 'api/user/logout';
  };

  const createError = useCallback(({ message, title, status }) => {
    setBackendError({
      active: true,
      message,
      title,
      status
    });
  }, []);

  React.useEffect(() => {
    const setup = async () => {
      try {
        const hasLicence = await getLicense();
        if (!hasLicence) {
          createError({
            message: t('general:noLicense'),
            title: t('general:noLicenseTitle'),
            status: '404'
          });
          setLoaded(true);
          return;
        }
      } catch (error) {
        createError({
          message: error.reason,
          title: error.title,
          status: '500'
        });
      }
      try {
        const tenantDatabases = await getTenants();
        let listOfTenants = [];
        await Promise.all(
          tenantDatabases.map(async tenant => {
            const tenantInstance = await getTenant(tenant.tenantId);
            listOfTenants.push(tenantInstance);
          })
        );
        setTenants(listOfTenants);
      } catch (error) {
        console.log(error);
      }

      setLoaded(true);
    };

    if (!loaded) {
      setup();
    }
  }, [createError, loaded, t]);

  if (!loaded) {
    return (
      <CenterDiv>
        <Spin size="large" tip={`${t('general:loading')}...`} />
      </CenterDiv>
    );
  }

  return backendError.active ? (
    <CenterDiv>
      <Result
        status={backendError.status}
        title={backendError.title}
        subTitle={backendError.message}
        extra={
          backendError.status === '403' ? (
            <Button onClick={onLogout} type="primary">
              {t('general:logout')}
            </Button>
          ) : null
        }
      />
    </CenterDiv>
  ) : (
    <Suspense
      fallback={
        <CenterDiv>
          <Spin size="large" />
        </CenterDiv>
      }
    >
      <Routes>
        {ApiAuthorizationRoutes.map((route, index) => {
          const { element, ...rest } = route;
          return <Route key={index} {...rest} element={element} />;
        })}
        {/* <Route path={ApplicationPaths.ApiAuthorizationPrefix} component={ApiAuthorizationRoutes} /> */}
        <Route path="/:tenantId" element={<AuthorizeRoute path="/:tenantId" element={<TenantHandler />} />}>
          <Route index path="*" element={<Navigate to="dashboard" />} />
          <Route
            path={`dashboard`}
            element={
              <Suspense
                fallback={
                  <CenterDiv>
                    <Spin size="large" />
                  </CenterDiv>
                }
              >
                <Dashboard />
              </Suspense>
            }
          />
          <Route
            path={`case/:caseType`}
            element={
              <Suspense
                fallback={
                  <CenterDiv>
                    <Spin size="large" />
                  </CenterDiv>
                }
              >
                <CaseHandler />
              </Suspense>
            }
          />
          <Route
            path={`case/:caseType/*`}
            element={
              <Suspense
                fallback={
                  <CenterDiv>
                    <Spin size="large" />
                  </CenterDiv>
                }
              >
                <CaseHandler />
              </Suspense>
            }
          />
          <Route
            path={`case`}
            element={
              <Suspense
                fallback={
                  <CenterDiv>
                    <Spin size="large" />
                  </CenterDiv>
                }
              >
                <CaseHandler />
              </Suspense>
            }
          />
          {user?.role === 'Admin' && (
            <Route path="settings" key="settingsRoute">
              <Route index key="settingsIndex" element={<Navigate to="general" />} />
              <Route
                path=":settingsId"
                key="settings"
                element={
                  <Suspense fallback={<Spin absolute translationKey={'general:settings'} />}>
                    <AdminSettings />
                  </Suspense>
                }
              />
            </Route>
          )}
        </Route>
        <Route index path="/" element={<SelectMunicipality tenants={tenants} />} />
      </Routes>
    </Suspense>
  );
};

export default App;
